import _ from 'underscore';
import React, { useCallback, useMemo } from 'react';
import { withError, withValidation } from '@bingads-webui-react/validation';
import { getTaxForm, getViewTaxForm, getMatchedTaxFormMappingItem } from './taxform-provider';
import { TaxFormPropTypes } from './shapes';
import { MTToUITaxCertificate, MTToUITaxInfo, UIToMTTaxCertificate, UIToMTTaxInfo } from './mt-utils';
import { getPilotStatus } from './utils';
import { useValidationI18n } from './hooks/use-i18n';
import { TaxFormMode, GlobalTaxIdPilots, requiredFeatures } from './constants';

const WrappedDiv = withError(withValidation(props => <div {...props} />));
/**
 * Tax form component rendering based on the Tax Format
 * @param {object} result validation result passed to the onchange callback
 * @param {object} props passed to the component
 * @returns {React.Component} Tax form corresponding to a given tax format
 */
export const TaxForm = ({
  i18n,
  data: taxDataMTFormatted,
  mtData: mtTaxDataMTFormatted,
  taxCertification,
  mtTaxCertification,
  taxFormat,
  mtTaxFormat,
  onValidate,
  showLineByLineFormGroup,
  taxFormMode,
  disabled,
  alwaysShowError,
  showTaxApprovalForm,
  ownerAccountId,
  urlBuilder,
  showTaxCertLink,
  permissions,
  countries,
  isSignupFlow,
  vatStatus,
  passiveErrors,
  isCPFRequired,
  accountMode,
  shouldHideBRUpdateTaxForm, // used by brazil tax form in add funds page for Boleto
  isXandrOnboarding, // use this flag for specific tax logic in Xandr customer onboarding workflow
}) => {
  const validationI18n = useValidationI18n(i18n);
  const isABLColombiaTaxEnabled = useMemo(() => getPilotStatus(requiredFeatures.ABLColombiaTax, permissions), [permissions]);
  const isABLAlaskaTaxEnabled = useMemo(() => getPilotStatus(requiredFeatures.ABLAlaskaTax, permissions), [permissions]);
  const isABLHawaiiTaxEnabled = useMemo(() => getPilotStatus(requiredFeatures.ABLHawaiiTax, permissions), [permissions]);
  const isABLGlobalTaxEnabled = useMemo(() => _.values(GlobalTaxIdPilots).some(pilotFlag => getPilotStatus(pilotFlag, permissions)), [permissions]);
  const isXandrCanadaTaxCertEnabled = useMemo(() => getPilotStatus(requiredFeatures.XandrCanadaTaxCertificate, permissions) && isXandrOnboarding, [permissions, isXandrOnboarding]);
  const isTaxCertEnabled = useMemo(() => isABLColombiaTaxEnabled || isABLAlaskaTaxEnabled || isABLHawaiiTaxEnabled || isABLGlobalTaxEnabled || isXandrCanadaTaxCertEnabled, [isABLAlaskaTaxEnabled, isABLColombiaTaxEnabled, isABLGlobalTaxEnabled, isABLHawaiiTaxEnabled, isXandrCanadaTaxCertEnabled]);

  const shouldUseMTTaxInfo = useMemo(() => getMatchedTaxFormMappingItem(taxFormat) === getMatchedTaxFormMappingItem(mtTaxFormat), [mtTaxFormat, taxFormat]);

  // Convert UI object to MT object while invoking the callback to parent component
  const onValidateCallBack = useCallback((result) => {
    onValidate({
      data: UIToMTTaxInfo(result.data),
      taxCertification: isTaxCertEnabled ? UIToMTTaxCertificate(result.taxCertification) : null,
      isValid: result.isValid,
    });
  }, [onValidate, isTaxCertEnabled]);


  // TODO: Explore Memoization to optimize the Conversion from MT to UI Object and vice versa
  const isViewMode = taxFormMode === TaxFormMode.ViewMode;
  const data = useMemo(
    () => MTToUITaxInfo(taxDataMTFormatted),
    [taxDataMTFormatted]
  );
  const mtData = useMemo(
    () => (shouldUseMTTaxInfo ? MTToUITaxInfo(mtTaxDataMTFormatted) : {}),
    [mtTaxDataMTFormatted, shouldUseMTTaxInfo]
  );
  const taxCert = useMemo(
    () => (isTaxCertEnabled ? MTToUITaxCertificate(taxCertification) : null),
    [taxCertification, isTaxCertEnabled]
  );
  const mtTaxCert = useMemo(
    () => (isTaxCertEnabled && shouldUseMTTaxInfo ? MTToUITaxCertificate(mtTaxCertification) : {
      taxCertifications: [],
      reverseChargeDocuments: [],
    }),
    [isTaxCertEnabled, shouldUseMTTaxInfo, mtTaxCertification]
  );
  const TaxFormComponent = useMemo(
    () => (isViewMode ?
      getViewTaxForm(taxFormat) :
      getTaxForm(taxFormat)),
    [isViewMode, taxFormat]
  );
  return (
    <WrappedDiv className="tax-form-container" validationProps={{ i18n: validationI18n, ...passiveErrors.Outer }}>
      <TaxFormComponent
        i18n={i18n}
        onValidate={onValidateCallBack}
        taxFormat={taxFormat}
        data={data}
        mtData={mtData}
        showLineByLineFormGroup={showLineByLineFormGroup}
        taxFormMode={taxFormMode}
        disabled={disabled}
        alwaysShowError={alwaysShowError}
        taxCertification={taxCert}
        mtTaxCertification={mtTaxCert}
        showTaxApprovalForm={showTaxApprovalForm}
        ownerAccountId={ownerAccountId}
        urlBuilder={urlBuilder}
        showTaxCertLink={showTaxCertLink}
        permissions={permissions}
        countries={countries}
        isSignupFlow={isSignupFlow}
        vatStatus={vatStatus}
        passiveErrors={passiveErrors}
        isCPFRequired={isCPFRequired}
        accountMode={accountMode}
        shouldHideBRUpdateTaxForm={shouldHideBRUpdateTaxForm}
        isXandrOnboarding={isXandrOnboarding}
      />
    </WrappedDiv>
  );
};

TaxForm.propTypes = TaxFormPropTypes;
TaxForm.defaultProps = {
  data: [],
  taxCertification: {},
  mtData: [],
  mtTaxCertification: {},
  onValidate: _.noop,
  showLineByLineFormGroup: false,
  taxFormMode: TaxFormMode.EditMode,
  disabled: false,
  alwaysShowError: false,
  showTaxApprovalForm: false,
  ownerAccountId: null,
  urlBuilder: {
    UrlBuilders: {},
    UrlHelper: {},
  },
  showTaxCertLink: false,
  countries: [],
  permissions: {},
  isSignupFlow: false,
  vatStatus: null,
  passiveErrors: {},
  isCPFRequired: false,
  mtAddress: {},
  accountMode: null,
  shouldHideBRUpdateTaxForm: false,
  isXandrOnboarding: false,
};
